import { Link } from 'react-router-dom';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { Form, Input, Select } from 'antd';
import JobPositionFormFields from '@/components/job-positions/JobPositionFormFields';
import { SettingSwitch } from '@/components/profile/settingCards';
import { ROLES_PERMISSIONS, ROUTES } from '@/constants';
import { useEnforceAccessControl } from '@/hooks';
import { cn } from '@/libs/utils';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';
import { Block, FieldGrid } from './profileBlocks';

/**
 * The "Sector and position" tile of the employee profile: the job (position
 * in both languages) and where it sits (company, sector, organisational unit,
 * the HR officer role). Same fields, rules and save as EmployeePersonalFields
 * and EmployeeContractFields, whose fields step aside on the profile.
 */
export default function EmployeeOrgProfilePanel() {
  const {
    COMPANY_DATA,
    companyDataIsLoading,
    companyIdKeys,
    companyIdState,
    companyIsLoading,
    companyOptions,
    companyOptionsData,
    employeeSectorKeys,
    employeeSectorState,
    form,
    hasCompanySector,
    isHrOfficerKeys,
    isHrOfficerState,
    orgUnitKeys,
    orgUnitState,
    positionEngKeys,
    positionEngState,
    positionKeys,
    positionState,
    readOnly,
    sectionClass,
    selectedCompany,
    t,
  } = useEmployeeGeneralDataForm();
  const f = (key) => t(key, { ns: 'forms' });
  const fv = (key) => t(`formValidation.${key}`, { ns: 'forms' });
  const tp = (key) => t(`employeeProfile.orgUi.${key}`, { ns: 'translations' });

  const showPosition = positionState?.visible || positionEngState?.visible;
  const canOpenSector = useEnforceAccessControl(ROLES_PERMISSIONS.SECTORS.VIEW);
  const sectorId = Form.useWatch(employeeSectorKeys, form);

  return (
    <div className={cn('grid max-w-[760px] gap-4', sectionClass('org'))}>
      {showPosition && (
        <Block title={tp('groupJob')}>
          {/* Titles run long, so each takes a whole row. */}
          {/* Picked from the company's job position catalog when it has one. */}
          <div className="grid">
            <JobPositionFormFields
              form={form}
              companyId={selectedCompany}
              positionKeys={positionKeys}
              positionEngKeys={positionEngKeys}
              positionState={positionState}
              positionEngState={positionEngState}
            />
          </div>
        </Block>
      )}

      <Block title={tp('groupPlace')}>
        <FieldGrid>
          {companyIdState?.visible && (
            <Form.Item
              name={companyIdKeys}
              label={f(
                companyOptionsData?.isSingleCompany
                  ? 'company'
                  : 'selectCompany'
              )}
              rules={[{ required: true, message: fv('companyRequired') }]}
            >
              <Select
                allowClear
                showSearch
                mode="single"
                placeholder={f('selectCompany')}
                className="w-full"
                optionFilterProp="label"
                disabled={
                  companyOptionsData?.isSingleCompany ||
                  companyIdState?.disabled
                }
                loading={companyIsLoading}
                options={companyOptions}
                // Sectors belong to one company: a sector kept from the
                // previous company is not among the new options.
                onChange={() =>
                  form.setFieldValue(employeeSectorKeys, undefined)
                }
              />
            </Form.Item>
          )}
          {hasCompanySector && employeeSectorState?.visible && (
            <Form.Item
              name={employeeSectorKeys}
              label={f('sector')}
              rules={[{ required: true, message: fv('sectorRequired') }]}
              extra={
                canOpenSector && sectorId ? (
                  <Link
                    className="font-semibold underline"
                    // With the company, so going back to the sectors list
                    // (breadcrumbs) opens that company, not all of them.
                    to={`${ROUTES.admin}/${ROUTES.sectors}/view/${sectorId}${
                      selectedCompany ? `?company=${selectedCompany}` : ''
                    }`}
                  >
                    {tp('openSector')}
                  </Link>
                ) : null
              }
            >
              <Select
                allowClear
                mode="single"
                placeholder={f('sector')}
                className="w-full"
                loading={companyDataIsLoading}
                options={COMPANY_DATA.sectors}
                disabled={employeeSectorState?.disabled}
              />
            </Form.Item>
          )}
          {orgUnitState?.visible && (
            <Form.Item
              name={orgUnitKeys}
              label={f('orgUnit')}
              extra={readOnly ? null : f('orgUnitHint')}
            >
              <Input
                allowClear
                disabled={orgUnitState?.disabled}
                placeholder={f('orgUnit')}
              />
            </Form.Item>
          )}
        </FieldGrid>
      </Block>

      {hasCompanySector && isHrOfficerState?.visible && (
        <Form.Item name={isHrOfficerKeys} noStyle>
          <SettingSwitch
            locked={readOnly || isHrOfficerState?.disabled}
            icon={<SafetyCertificateOutlined />}
            title={`${f('hrOfficer')} (${f('role').toLowerCase()})`}
          />
        </Form.Item>
      )}
    </div>
  );
}
